import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { capitalize } from "lodash";
import { LuPrinter } from "react-icons/lu";
import { GenericTable, TableColumn } from "../ui/table";
import useFetchUserBookingSummary from "./hooks/useFetchUserBookingSummary";
import useShowToast from "../../hooks/useShowToast";
import api from "../../api";
import { formatCurrency } from "../../utils/numbers";

const ClubManagementUserBookings = (): ReactElement => {
  const { t } = useTranslation();
  const currentDate = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
  // 0 represents "all months", 1-12 represents specific months
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const { errorToast } = useShowToast();

  // Convert year/month to date range
  // If selectedMonth is 0, use entire year; otherwise use specific month
  const dateFrom = useMemo(() => {
    if (selectedMonth === 0) {
      return startOfYear(new Date(selectedYear, 0, 1));
    }
    return startOfMonth(new Date(selectedYear, selectedMonth - 1, 1));
  }, [selectedYear, selectedMonth]);

  const dateTo = useMemo(() => {
    if (selectedMonth === 0) {
      return endOfYear(new Date(selectedYear, 11, 31));
    }
    return endOfMonth(new Date(selectedYear, selectedMonth - 1, 1));
  }, [selectedYear, selectedMonth]);

  const { userBookingSummary, loading } = useFetchUserBookingSummary(dateFrom, dateTo);

  const years = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, [currentDate]);

  const months = useMemo(() => {
    const months: { id: number; name: string }[] = [];
    for (let i = 0; i < 12; i++) {
      months.push({
        id: i + 1,
        name: capitalize(
          new Date(selectedYear, i, 1).toLocaleDateString("it-IT", { month: "long" }),
        ),
      });
    }
    return months;
  }, [selectedYear]);

  const resetFilters = () => {
    setSelectedYear(currentDate.getFullYear());
    setSelectedMonth(0);
  };

  const handlePrintSummary = async () => {
    setIsPrintLoading(true);
    await api.printUserBookingSummary(dateFrom, dateTo);
    if (api.error) {
      errorToast({
        title: t("club_management.user_bookings.print.error_title"),
        description: t("club_management.user_bookings.print.error_description"),
      });
    }
    setIsPrintLoading(false);
  };

  const columns: TableColumn[] = useMemo(
    () => [
      {
        label: t("club_management.user_bookings.columns.username"),
        key: "username",
        sortable: true,
      },
      {
        label: t("club_management.user_bookings.columns.name"),
        key: "name",
        sortable: true,
      },
      {
        label: t("club_management.user_bookings.columns.surname"),
        key: "surname",
        sortable: true,
      },
      {
        label: t("club_management.user_bookings.columns.number_of_bookings"),
        key: "total_bookings",
        sortable: true,
      },
      {
        label: t("club_management.user_bookings.columns.number_of_paid_bookings"),
        key: "total_paid_bookings",
        sortable: true,
      },
      {
        label: t("club_management.user_bookings.columns.amount_paid"),
        key: "total_amount",
        sortable: true,
        render: (value: unknown) => <Text> {formatCurrency(value as number)}</Text>,
      },
    ],
    [t],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        gap={3}
        width="100%"
      >
        <Heading size="lg">{t("club_management.user_bookings.title")}</Heading>
        <Button
          leftIcon={<LuPrinter />}
          colorScheme="blue"
          size="sm"
          onClick={handlePrintSummary}
          isLoading={isPrintLoading}
        >
          {t("club_management.user_bookings.actions.print")}
        </Button>
      </Box>

      {/* Year and Month Filters */}
      <Box p={4} bg="gray.50" borderRadius="md">
        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          gap={4}
          alignItems={{ base: "stretch", md: "end" }}
        >
          <FormControl>
            <FormLabel>{t("club_management.user_bookings.filters.year")}</FormLabel>
            <Select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              size="sm"
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>{t("club_management.user_bookings.filters.month")}</FormLabel>
            <Select
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
              size="sm"
            >
              <option value={0}>{t("club_management.user_bookings.filters.all_months")}</option>
              {months.map(month => (
                <option key={month.id} value={month.id}>
                  {month.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <Button onClick={resetFilters} size="sm" pl={8} pr={8} colorScheme="blue">
            {t("club_management.user_bookings.filters.reset")}
          </Button>
        </Box>
      </Box>

      {/* User Bookings Table */}
      <GenericTable
        data={userBookingSummary as unknown as Record<string, unknown>[]}
        columns={columns}
        size="sm"
      />
    </VStack>
  );
};

export default ClubManagementUserBookings;
